/** @module csi/view/Tooltip */
define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/dom",
	"dojo/dom-class",
	"dojo/when",
	"csi/def/Actions",
	"csi/heading/_base",
	"csi/html",
	"csi/service/imageService",
	"csi/state",
	"csi/view",
	"csi/widget/Tooltip"
], function(array, declare, lang, dom, domClass, when, Actions, csiHeading, html, {waitForImagesToLoad}, state, csiView, Tooltip) {

	/**
	 * @typedef {Object} TooltipTargetParams
	 * Object holding parameters necessary to create tooltip for a given target node
	 *
	 * @property {DOMNode} connectNode - node to which tooltip should be connected
	 * @property {DOMNode} [content] - content for tooltip  
	 * @property {string} [css] - root CSS class for tooltip  
	 * @property {number} [delay] - timeout delay to wait before showing tooltip (default is 500ms)
	 * @property {string[]} [position] - desired positions for tooltip relative to connectNode in order of importance (i.e. "above-centered", "below-centered", ...)
	 * @property {DOMNode} refNode - node for which tooltip is generated for
	 *
	 * @property {Context} [ctx] - Context used to render Heading content
	 * @property {Heading} [h] - Heading used to render content in target cell
	 * @property {Renderer} [ren] - Renderer class used to render Heading
	 */

	return declare(Tooltip, {
		/** @property {Actions} defActions - actions used in tooltip */
		defActions: null,

		/** @property {DOMNode} refNode - container node for heading holding context */
		refNode: null,

		/** @property {Integer} fadeDelay - time for tooltip to fade in/out */
		fadeDelay: 200,

		/** @property {Boolean} bOnHover - if true then view case, otherwise it's form case */
		bOnHover: true,

		/** @property {csi.view.Widget} view - parent view */
		view: null,

		/** @property {DOMNode} _contentNode - content for tooltip */
		_contentNode: null,

		/** @property {Object} _onDeck - on-deck parameters (actions, refNode, connectNode) */
		_onDeck: null,

		/** @property {DOMNode} DOMNode - root node under which to check for tooltips */
		_rootNode: null,

		/**
		 * Add action to tooltip
		 * @param {Action} a
		 * @param {Number} nIndex
		 */
		addAction(a, nIndex) {
			var x = this._onDeck;
			if (x && a) {
				x.defActions = x.defActions || new Actions();
				x.defActions.addAction(a, nIndex);
			}
		},

		/**
		 * Add tooltip content
		 * @param {DOMNode} node the node which will be added 
		 * @return {DOMNode}
		 */
		addContent(node) {
			this._contentNode = this._contentNode || html.create("div", {"class": "csi-tooltip-content"});
			this._contentNode.appendChild(node);

			return node;
		},

		/**
		 * Add labeled div to tooltip content
		 * @param {string} label
		 * @param {string} [css] - css for div
		 * @return {DOMNode} return created div where to add content
		 */
		addLabeledContent(label, css) {
			const root = html.create("div");

			this.addContent(root);

			html.create("label", {
				"class": "bold",
				innerText: label + ":"
			}, root);

			return html.create("div", {"class": css || ""}, root);
		},

		/**
		 * Force open tooltip
		 * @param {TooltipTargetParams} params
		 */
		forceOpen(refNode, params) {
			if (!Tooltip.isOpen()) {
				const tooltipParams = this._getTargetParams(refNode);
				const {connectNode} = tooltipParams;
				this._onDeck = {connectNode, refNode};
				this.open(refNode, params);
			}
		},

		/**
		 * Return on deck refNode when rendering actions
		 */
		getRefNodeForAction() {
			return this._onDeck ? this._onDeck.refNode : null;
		},

		/**
		 * Generate the content based on given params
		 * @param {TooltipTargetParams} params
		 * @return {Deferred}
		 */
		generateTooltipFor: async function(params) {
			const content = params.content;
			const ren = params.ren;

			if (content) {
				this.addContent(content);
			}

			return ren ? ren.headingToTooltip(params.ctx, params.h, this) : null;
		},

		/**
		 * set tooltip content explicitly
		 * @param {Widget|DOMNode|String} node
		 */
		setContent(node) {
			this._contentNode = (typeof node === "string") ? html.create("div", {innerText: node}) : (node.domNode || node);
			domClass.add(this._contentNode, "csi-tooltip-content");
		},

		/**
		 * Override base class open to compose the tooltip before it is displayed
		 * @param {DOMNode} target
		 * @param {TooltipTargetParams} params
		 */
		open(target, params) {
			var view = this.view;
			var domNode = this.domNode;

			this._clearTimer();

			// do not display view tooltips if view is not valid or non-modal action is in process
			if (!target || !view || !view.isValidView() || state.get("modelessAction")) {
				return;
			}

			// reset to defaults
			this.fadeDelay = 200;
			html.destroyChildren(domNode);

			const onDeck = () => {
				var x = this._onDeck;
				if (x && view.isValidView()) {
					return x;
				}

				this.close();
				return null;
			}

			when(this.generateTooltipFor(params), () => {
				var ctx = params.ctx;
				var x = onDeck();
				if (!x) {
					return;
				}

				// if content for tooltip, then add to DOM
				if (this._contentNode) {
					domNode.appendChild(this._contentNode);
				}

				// don't allow any tooltip actions while upload in progress
				if (x.defActions) {
					x.defActions.toHtml(ctx, domNode);
				}

				this._contentNode = null;
				this.position = params.position;

				// display tooltip with content from this tooltip
				if (domNode.firstChild) {
					when(waitForImagesToLoad(), () => {
						var x = onDeck();
						if (x) {
							Tooltip.applyMuiStyling(view.useMuiTooltipStyling);
							Tooltip.show(this, x.connectNode, this.position, !this.isLeftToRight(), this.textDir, true);
						}
					});
				} else {
					this.close();
				}
			});
		},

		/**
		 * Start hiding tooltip - because it can be sticky, we cannot rely on it
		 */
		close() {
			// truly being hidden until onFadeOut is called
			if (this._connectNode) {
				Tooltip.hide(this._connectNode);
			}
		},

		/**
		 * invoke action on tooltip
		 */
		onClick(e) {
			if (this.defActions) {
				this.view.onClick(e);
			}
		},

		/**
		 * Copy parameters from onDeck to in-use
		 */
		onFadeIn() {
			if (this._onDeck) {
				this._connectNode = this._onDeck.connectNode;
				this.defActions = this._onDeck.defActions;
				this.refNode = this._onDeck.refNode;
			}

			this._clearTimer();
			this._onDeck = null;

			if (this._connectNode) {
				this.onShow(this._connectNode, this.position);
			}
		},

		/**
		 * Tooltip is finally hidden, so now reset members
		 */
		onFadeOut() {
			this.defActions = null;
			this.refNode = null;
			this._connectNode = null;
			this.onHide();
		},

		_clearTimer() {
			if (this._showTimer) {
				clearTimeout(this._showTimer);
				delete this._showTimer;
			}
		},

		/**
		 * Given target, return params with context, heading, refNode and renderer if valid
		 * @param {DOMNode} target
		 * @return {TooltipTargetParams}
		 */
		_getTargetParams(target) {
			var refNode = csiHeading.findParentHeadingNode(target, this._rootNode);
			if (refNode) {
				var ctx = this.view.getContext();
				var h = this.view.getHeading(csiHeading.getHeadingNodeMarker(refNode, ctx), refNode);
				if (h) {
					var ren = csiHeading.getHeadingRendererInContext(ctx, h);
					if (ren) {
						ctx.bTooltip = true;
						return ren.getTooltipParams(ctx, h, refNode);
					}
				}
			}

			return null;
		},

		/**
		 * Determine connectNode and showDelay for renderer
		 * @param {Event} e
		 */
		_onTargetMouseOver(e) {
			var target = e.target;
			var view = csiView.getEnclosingView(target);
			if (view != this.view) {
				return;
			}

			var params = this._getTargetParams(target);
			if (params) {
				var oldConnectNode = this._onDeck ? this._onDeck.connectNode : this._connectNode;
				var connectNode = params.connectNode;

				// test if within new connect node, and if it is different than old connect node that is already being displayed
				if (dom.isDescendant(target, connectNode) && (connectNode != oldConnectNode)) {
					// if mouse is within connect node for a heading, then start timer to show tooltip
					this._onDeck = {
						connectNode: connectNode,
						refNode: params.refNode
					};

					this._clearTimer();
					this._showTimer = setTimeout(lang.hitch(this, this.open, target, params), params.delay || Tooltip.TOOLTIP_SHOW_DELAY);
				}
			}
		},

		/**
		 * If mouse is no longer within refNode for which tooltip is currently displayed, then hide
		 * @param {Event} e
		 */
		_onTargetMouseOut(e) {
			// the event related target indicates what node was entered, so better to test if node entered is outside the desired node
			// rather than testing the node that was exited. Maybe it would be better to just connect to "onmouseenter"?
			var domNodeIn = e.relatedTarget;
			var refNode = this.refNode;

			if (refNode && (domNodeIn != refNode) && !dom.isDescendant(domNodeIn, refNode)) {
				this.close();
			}

			// if tooltip on deck is now not for node under mouse, then stop it from showing
			refNode = this._onDeck ? this._onDeck.refNode : null;

			if (refNode && (domNodeIn != refNode) && !dom.isDescendant(domNodeIn, refNode)) {
				this._clearTimer();
				this._onDeck = null;
			}
		},

		/**
		 * DOJO INTERNAL: connect to onmouseover/out rather than on enter/leave - assuming only one connectId
		 * @param {String} newId
		 */
		_setConnectIdAttr(newId) {

			// Remove connections to old nodes (if there are any)
			array.forEach(this._connections || [], function(nested) {
				array.forEach(nested, lang.hitch(this, "disconnect"));
			}, this);

			// Make array of id's to connect to, excluding entries for nodes that don't exist yet, see startup()
			this._connectIds = array.filter(lang.isArrayLike(newId) ? newId : (newId ? [newId] : []),
				function(id) {return dom.byId(id);});

			// Make connections
			this._connections = this.bOnHover ? array.map(this._connectIds, function(id) {
				var node = this._rootNode = dom.byId(id);
				return node ? [
					this.connect(node, "onmouseover", "_onTargetMouseOver"),
					this.connect(node, "onmouseout", "_onTargetMouseOut")
				] : [];
			}, this) : null;

			this._set("connectId", newId);
		}
	});
});